// src/admin/useAdminData.js
import { createContext, useContext, useState, useEffect } from 'react';
import axios from 'axios';

export const API_URL = 'https://consult.conversationaily.ai/api/consultation/data';

export const AdminDataContext = createContext(null);

// Recursively find the first array anywhere in a parsed JSON object
function findArray(obj, depth = 0) {
  if (depth > 4) return null;
  if (Array.isArray(obj)) return obj;
  if (obj && typeof obj === 'object') {
    for (const val of Object.values(obj)) {
      const found = findArray(val, depth + 1);
      if (found) return found;
    }
  }
  return null;
}

export function AdminDataProvider({ children }) {
  const [state, setState] = useState({
    rows: null,        // raw array of objects from API
    loading: true,
    error: null,
    debugInfo: null,   // shown in UI when something goes wrong
  });

  useEffect(() => {
    setState({ rows: null, loading: true, error: null, debugInfo: null });

    axios.get(API_URL, {
      headers: { Accept: 'application/json' },
      transformResponse: [d => d],
    })
      .then(res => {
        const rawText = String(res.data || '');
        const preview = rawText.slice(0, 600);

        let parsed;
        try {
          parsed = JSON.parse(rawText);
        } catch {
          return setState({
            rows: null, loading: false,
            error: 'API returned non-JSON',
            debugInfo: { status: res.status, preview },
          });
        }

        // Find the array — works for [], {data:[]}, {results:[]}, any nesting
        const rows = findArray(parsed);

        if (!rows || rows.length === 0) {
          return setState({
            rows: null, loading: false,
            error: rows ? 'API returned an empty array' : 'No array found in response',
            debugInfo: {
              status: res.status,
              topLevelKeys: parsed && typeof parsed === 'object' ? Object.keys(parsed) : typeof parsed,
              preview: JSON.stringify(parsed).slice(0, 600),
            },
          });
        }

        // Success — pass raw rows straight through; Dashboard does all processing
        setState({
          rows,
          loading: false,
          error: null,
          debugInfo: {
            status: res.status,
            rowCount: rows.length,
            keys: Object.keys(rows[0] || {}),
          },
        });
      })
      .catch(err => {
        const isCORS = !err.response;
        setState({
          rows: null, loading: false,
          error: isCORS
            ? `CORS / Network error — the browser blocked the request.\n\nFix: add these headers to the Flask route:\n  Access-Control-Allow-Origin: *\n  Access-Control-Allow-Methods: GET, OPTIONS\n  Access-Control-Allow-Headers: Content-Type\n\nOriginal error: ${err.message}`
            : `HTTP ${err.response?.status}: ${JSON.stringify(err.response?.data).slice(0, 200)}`,
          debugInfo: { isCORS, message: err.message },
        });
      });
  }, []);

  return (
    <AdminDataContext.Provider value={state}>
      {children}
    </AdminDataContext.Provider>
  );
}

export function useAdminData() {
  return useContext(AdminDataContext);
}